import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../lib/cart";
import { CheckCircle, Package, ArrowRight } from "@phosphor-icons/react";
import { WhatsAppShareButton } from "../components/WhatsAppWidget";

export default function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { clear } = useCart();
  const [status, setStatus] = useState("polling"); // polling | paid | expired | error
  const [result, setResult] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let cancelled = false;
    const poll = async (count = 0) => {
      if (cancelled) return;
      if (count >= 10) {
        setStatus("error");
        return;
      }
      try {
        const { data } = await api.get(`/orders/payment-status/${sessionId}`);
        if (cancelled) return;
        setResult(data);
        if (data.payment_status === "paid" || data.status === "paid") {
          setStatus("paid");
          clear();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        setAttempts(count + 1);
        setTimeout(() => poll(count + 1), 2000);
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.warn("[order-success] payment-status poll failed:", err);
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <div className="pt-24 pb-24 max-w-2xl mx-auto px-4 text-center" data-testid="order-success-page">
      {status === "polling" && (
        <div>
          <div className="w-16 h-16 rounded-full border-4 border-[#A94438]/20 border-t-[#A94438] animate-spin mx-auto mb-5" />
          <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tighter mb-2">Confirming payment...</h1>
          <p className="text-sm text-[#5C5C54]">Please don't close this page. Attempt {attempts + 1} of 10.</p>
        </div>
      )}

      {status === "paid" && (
        <div data-testid="order-success-confirmed">
          <div className="w-20 h-20 rounded-full bg-[#2B5329]/10 flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={42} weight="fill" className="text-[#2B5329]" />
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-black tracking-tighter mb-3">Order confirmed!</h1>
          <p className="text-sm md:text-base text-[#5C5C54] mb-2">Thank you for shopping with AfriPantry.</p>
          <p className="text-sm text-[#5C5C54] mb-6">
            {result?.order_ids?.length > 1 ? `We've created ${result.order_ids.length} split orders` : "We've created your order"} — one for each vendor. They'll start preparing right away.
          </p>

          <div className="p-3 rounded-2xl bg-[#E4A951]/10 border border-[#E4A951]/40 text-xs text-[#5C5C54] max-w-md mx-auto mb-6">
            <span className="font-bold text-[#1A1A18]">Heads up:</span> Items from different vendors may arrive separately and may have different delivery times.
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-5">
            <Link to="/orders" data-testid="success-track-orders" className="btn-primary gap-2">
              <Package size={16} weight="duotone" /> Track my orders
            </Link>
            <Link to="/" data-testid="success-keep-shopping" className="btn-secondary gap-2">
              Keep shopping <ArrowRight size={14} weight="bold" />
            </Link>
          </div>

          <div className="pt-5 border-t border-[#E5E2D9] max-w-md mx-auto">
            <p className="text-xs text-[#5C5C54] mb-2">Need help? We're here for you.</p>
            <WhatsAppShareButton
              message="Hi AfriPantry, I just placed an order and need help."
              label="Chat with us on WhatsApp"
              testid="success-whatsapp-help"
            />
          </div>
        </div>
      )}

      {status === "expired" && (
        <div data-testid="order-success-expired">
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-3">Checkout expired</h1>
          <p className="text-sm text-[#5C5C54] mb-6">Your Stripe session timed out. Please try again.</p>
          <Link to="/cart" className="btn-primary">Back to cart</Link>
        </div>
      )}

      {status === "error" && (
        <div data-testid="order-success-error">
          <h1 className="font-heading text-3xl font-black tracking-tighter mb-3">Payment verification issue</h1>
          <p className="text-sm text-[#5C5C54] mb-6">We couldn't confirm your payment yet. Please check your orders page in a moment.</p>
          <Link to="/orders" className="btn-primary">View my orders</Link>
        </div>
      )}
    </div>
  );
}
